import { FormEvent, useCallback, useState } from 'react';
import { ApiError, api } from '../api.js';
import type { Product } from '@csoki/shared';
import { eurToCents, centsToEurString } from '@csoki/shared';
import { useBarcodeScanner } from '../hooks/useBarcodeScanner.js';

type Mode = 'idle' | 'scanning' | 'form';

export function SellerUpload() {
  const [mode, setMode] = useState<Mode>('idle');
  const [barcode, setBarcode] = useState('');
  const [product, setProduct] = useState<Product | null>(null);
  const [priceStr, setPriceStr] = useState('');
  const [stock, setStock] = useState(1);
  const [err, setErr] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Editable product fields (for manual entry / editing OFF prefill).
  const [name, setName] = useState('');
  const [brand, setBrand] = useState('');
  const [imageUrl, setImageUrl] = useState('');

  const lookup = useCallback(async (code: string) => {
    setErr(null);
    setSavedMsg(null);
    setBarcode(code);
    try {
      const { product } = await api<{ product: Product }>(
        'GET',
        `/api/products/${encodeURIComponent(code)}`,
      );
      setProduct(product);
      setName(product.name);
      setBrand(product.brand ?? '');
      setImageUrl(product.imageUrl ?? '');
      setMode('form');
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        setProduct(null);
        setName('');
        setBrand('');
        setImageUrl('');
        setMode('form');
      } else {
        setErr('Could not look up product.');
      }
    }
  }, []);

  const onDetected = useCallback(
    (text: string) => {
      lookup(text.trim());
    },
    [lookup],
  );
  const scanner = useBarcodeScanner({ active: mode === 'scanning', onDetected });

  async function save(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setSavedMsg(null);
    setBusy(true);
    try {
      const priceCents = eurToCents(priceStr);
      let productBarcode = product?.barcode ?? barcode;
      // If the product doesn't exist yet (OFF miss or no barcode at all), create a manual one.
      if (!product) {
        if (!name.trim()) throw new Error('Name required');
        const created = await api<{ product: Product }>('POST', '/api/products', {
          name: name.trim(),
          brand: brand.trim() || null,
          imageUrl: imageUrl.trim() || null,
        });
        productBarcode = created.product.barcode;
      }
      await api('POST', '/api/listings', {
        productBarcode,
        priceCents,
        stock: Number(stock),
      });
      setSavedMsg(`Saved ${stock}× “${name}” at €${centsToEurString(priceCents)}.`);
      setMode('idle');
      setBarcode('');
      setProduct(null);
      setPriceStr('');
      setStock(1);
      setName('');
      setBrand('');
      setImageUrl('');
    } catch (e) {
      if (e instanceof ApiError && e.code === 'validation_error') {
        const issues = e.issues as { path: (string | number)[]; message: string }[];
        setErr(issues[0]?.message ?? 'Check the fields.');
      } else {
        setErr((e as Error).message || 'Could not save.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <h1>Upload an item</h1>

      {mode === 'idle' && (
        <div className="choice">
          <button onClick={() => setMode('scanning')}>Scan barcode</button>
          <button
            onClick={() => {
              const code = prompt('Enter barcode (EAN-13):') ?? '';
              if (code) lookup(code.trim());
            }}
          >
            Enter barcode
          </button>
          <button
            onClick={() => {
              setBarcode('');
              setProduct(null);
              setName('');
              setBrand('');
              setImageUrl('');
              setMode('form');
            }}
          >
            Add without barcode
          </button>
          {savedMsg && <div className="ok">{savedMsg}</div>}
        </div>
      )}

      {mode === 'scanning' && (
        <div className="scanner">
          <video ref={scanner.videoRef} playsInline muted />
          {scanner.err && <div className="err">Camera: {scanner.err}</div>}
          <button onClick={() => setMode('idle')}>Cancel</button>
        </div>
      )}

      {mode === 'form' && (
        <form onSubmit={save} className="form">
          {product?.imageUrl || imageUrl ? (
            <img className="thumb" src={product?.imageUrl ?? imageUrl} alt="" />
          ) : null}
          <label>
            Name
            <input value={name} onChange={(e) => setName(e.target.value)} required />
          </label>
          <label>
            Brand
            <input value={brand} onChange={(e) => setBrand(e.target.value)} />
          </label>
          <label>
            Image URL
            <input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} />
          </label>
          {barcode && (
            <div className="hint">
              Barcode: <code>{barcode}</code>
            </div>
          )}
          <label>
            Price (EUR)
            <input
              inputMode="decimal"
              value={priceStr}
              onChange={(e) => setPriceStr(e.target.value)}
              placeholder="3.50"
              required
            />
          </label>
          <label>
            Quantity in stock
            <input
              type="number"
              min={0}
              value={stock}
              onChange={(e) => setStock(Number(e.target.value))}
              required
            />
          </label>
          {err && <div className="err">{err}</div>}
          <div className="row">
            <button type="button" onClick={() => setMode('idle')}>
              Back
            </button>
            <button type="submit" disabled={busy}>
              {busy ? '…' : 'Save'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
